export const PREVIEW_ORG_ID = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";
export const PRIMARY_PROJECT_ID = "a1111111-1111-4111-8111-111111111111";

export type AppRole = "org_admin" | "reviewer" | "editor" | "viewer";

export type SessionContext = {
  userId: string;
  email: string;
  organizationId: string;
  organizationName: string;
  role: AppRole;
  fullName: string;
  jobTitle: string | null;
  credentials: string | null;
};

export const roleLabels: Record<AppRole, string> = {
  org_admin: "Organization admin",
  reviewer: "EP reviewer",
  editor: "Researcher / editor",
  viewer: "Viewer / client",
};

export const roleDescriptions: Record<AppRole, string> = {
  org_admin: "Organization, billing, templates, users and every project",
  reviewer: "Evidence review, professional decisions, approval and release",
  editor: "Project intake, source research and factual report drafting",
  viewer: "Read-only project and released-report access",
};

export const demoPersonas: Record<AppRole, { email: string; name: string }> = {
  org_admin: { email: "admin@preview.phaseone.app", name: "Maya Chen" },
  reviewer: { email: "reviewer@preview.phaseone.app", name: "Dr. Lena Ortiz, P.E." },
  editor: { email: "editor@preview.phaseone.app", name: "Jon Bell" },
  viewer: { email: "viewer@preview.phaseone.app", name: "Avery Brooks" },
};

export type PreviewRoute = {
  label: string;
  href: string;
  flow: string;
  roles: AppRole[] | "public";
};

export const previewRoutes: Array<{ group: string; routes: PreviewRoute[] }> = [
  {
    group: "Public journey",
    routes: [
      { label: "Marketing home", href: "/", flow: "Discover", roles: "public" },
      { label: "Pricing", href: "/pricing", flow: "Evaluate", roles: "public" },
      { label: "Security", href: "/security", flow: "Trust", roles: "public" },
      { label: "Sign in", href: "/sign-in", flow: "Access", roles: "public" },
    ],
  },
  {
    group: "Workspace journey",
    routes: [
      { label: "Dashboard", href: "/app", flow: "Orient", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Projects", href: "/app/projects", flow: "Portfolio", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "New project", href: "/app/projects/new", flow: "Intake", roles: ["org_admin", "reviewer", "editor"] },
      { label: "Overview", href: `/app/projects/${PRIMARY_PROJECT_ID}/overview`, flow: "Scope", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Sources", href: `/app/projects/${PRIMARY_PROJECT_ID}/sources`, flow: "Research", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Evidence", href: `/app/projects/${PRIMARY_PROJECT_ID}/evidence`, flow: "Verify", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Report", href: `/app/projects/${PRIMARY_PROJECT_ID}/report`, flow: "Draft", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Review", href: `/app/projects/${PRIMARY_PROJECT_ID}/review`, flow: "Decide", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Versions", href: `/app/projects/${PRIMARY_PROJECT_ID}/versions`, flow: "Compare", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Release", href: `/app/projects/${PRIMARY_PROJECT_ID}/release`, flow: "Approve", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Billing", href: "/app/billing", flow: "Subscribe", roles: ["org_admin", "reviewer", "editor", "viewer"] },
      { label: "Profile & MFA", href: "/app/settings/profile", flow: "Secure", roles: ["org_admin", "reviewer", "editor", "viewer"] },
    ],
  },
  {
    group: "Admin journey",
    routes: [
      { label: "Admin overview", href: "/admin", flow: "Operate", roles: ["org_admin"] },
      { label: "Users", href: "/admin/users", flow: "Authorize", roles: ["org_admin"] },
      { label: "Templates", href: "/admin/templates", flow: "Standardize", roles: ["org_admin"] },
      { label: "Source connectors", href: "/admin/sources", flow: "Connect", roles: ["org_admin"] },
      { label: "Content", href: "/admin/content", flow: "Publish", roles: ["org_admin"] },
      { label: "Analytics", href: "/admin/analytics", flow: "Measure", roles: ["org_admin"] },
      { label: "Audit", href: "/admin/audit", flow: "Trace", roles: ["org_admin"] },
    ],
  },
];

export const projectStages = [
  { key: "overview", label: "Overview" },
  { key: "sources", label: "Sources" },
  { key: "evidence", label: "Evidence" },
  { key: "report", label: "Report" },
  { key: "review", label: "Review" },
  { key: "versions", label: "Versions" },
  { key: "release", label: "Release" },
] as const;

export function canContribute(role: AppRole) {
  return role !== "viewer";
}

export function canReview(role: AppRole) {
  return role === "org_admin" || role === "reviewer";
}

export function canAdmin(role: AppRole) {
  return role === "org_admin";
}

